import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Save, User, Linkedin, FileText, CheckCircle2, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ bio: '', skills: [], resume_url: '', linkedin_url: '', looking_for_job: true });
  const [skillInput, setSkillInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/profile`, { withCredentials: true })
      .then(r => {
        if (r.data?.profile) {
          setProfile({ ...profile, ...r.data.profile });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addSkill = () => {
    if (skillInput.trim() && !profile.skills.includes(skillInput.trim())) {
      setProfile({ ...profile, skills: [...profile.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setProfile({ ...profile, skills: profile.skills.filter(s => s !== skill) });
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/profile`, profile, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert('Failed to save profile');
    }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Loading profile...</div>;

  return (
    <div data-testid="profile-page" className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Graduate Profile</p>
        <h1 className="text-3xl font-bold tracking-tight text-[#0077B6] mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Your Professional Profile
        </h1>
        <p className="text-base text-zinc-300 mb-10">This profile is visible to employers looking to hire IoA USA&trade; graduates.</p>

        <form onSubmit={saveProfile} className="space-y-8">
          {/* Bio */}
          <div>
            <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Professional Bio</label>
            <textarea
              data-testid="profile-bio"
              value={profile.bio}
              onChange={e => setProfile({ ...profile, bio: e.target.value })}
              rows={5}
              className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500 resize-none"
              placeholder="Tell employers about your background, interests, and career goals..."
            />
          </div>

          {/* Skills */}
          <div>
            <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Skills</label>
            <div className="flex gap-2 mb-3">
              <input
                data-testid="profile-skill-input"
                value={skillInput}
                onChange={e => setSkillInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                className="flex-1 px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500"
                placeholder="Type a skill and press Enter (e.g., Python, TensorFlow, SQL)"
              />
              <button type="button" onClick={addSkill} className="brand-btn px-4 py-2 text-sm">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((s, i) => (
                <span key={i} className="flex items-center gap-1.5 text-sm font-mono px-3 py-1.5 bg-[#0077B6]/10 text-[#0077B6] border border-[#0077B6]/20" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {s}
                  <button type="button" onClick={() => removeSkill(s)} className="text-[#F59E0B] hover:text-red-400 ml-1">&times;</button>
                </span>
              ))}
              {profile.skills.length === 0 && <p className="text-sm text-zinc-400 italic">No skills added yet</p>}
            </div>
          </div>

          {/* LinkedIn */}
          <div>
            <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>LinkedIn URL</label>
            <div className="flex items-center bg-[#111111] border border-[#262626] focus-within:border-[#0077B6]">
              <Linkedin className="w-5 h-5 text-zinc-400 ml-4" strokeWidth={1.5} />
              <input
                data-testid="profile-linkedin"
                value={profile.linkedin_url}
                onChange={e => setProfile({ ...profile, linkedin_url: e.target.value })}
                className="flex-1 px-3 py-3 bg-transparent outline-none text-base text-white placeholder-zinc-500"
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>
          </div>

          {/* Resume URL */}
          <div>
            <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Resume / Portfolio URL</label>
            <div className="flex items-center bg-[#111111] border border-[#262626] focus-within:border-[#0077B6]">
              <FileText className="w-5 h-5 text-zinc-400 ml-4" strokeWidth={1.5} />
              <input
                data-testid="profile-resume"
                value={profile.resume_url}
                onChange={e => setProfile({ ...profile, resume_url: e.target.value })}
                className="flex-1 px-3 py-3 bg-transparent outline-none text-base text-white placeholder-zinc-500"
                placeholder="Link to your resume or portfolio"
              />
            </div>
          </div>

          {/* Job Seeking Toggle */}
          <div className="flex items-center gap-4 p-5 bg-[#111111] border border-[#262626]">
            <Briefcase className="w-10 h-10 text-[#0077B6]" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="text-base text-white font-medium">Open to job opportunities</p>
              <p className="text-sm text-zinc-400">Allow employers to find and contact you about positions</p>
            </div>
            <button
              type="button"
              data-testid="profile-job-toggle"
              onClick={() => setProfile({ ...profile, looking_for_job: !profile.looking_for_job })}
              className={`w-12 h-7 rounded-full transition-colors relative ${profile.looking_for_job ? 'bg-[#0077B6]' : 'bg-[#333]'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${profile.looking_for_job ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Save */}
          <div className="flex items-center gap-4">
            <button type="submit" disabled={saving} data-testid="profile-save" className="brand-btn text-base px-8 py-3 disabled:opacity-50 flex items-center gap-2">
              {saving ? 'Saving...' : 'Save Profile'} <Save className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {saved && (
              <span className="flex items-center gap-1.5 text-green-400 text-sm">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Profile saved!
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
